import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Shield, CheckCircle, FileText, Download, ArrowRight } from 'lucide-react';

const Warranty = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO 
        title="10-Year Home Warranty | EC Home Builds Columbus Ohio"
        description="Every EC Home Build includes comprehensive 10-year structural warranty backed by RWC. Protection for your investment with insurance-backed coverage."
        keywords="home warranty Columbus Ohio, structural warranty, new home warranty, builder warranty"
        path="/warranty"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="w-20 h-20 bg-[#DAA520]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="text-[#DAA520]" size={48} />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-[#262626] mb-6">
              10-Year Warranty Protection
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Every EC Home Build comes with comprehensive warranty coverage backed by Residential Warranty Company (RWC) and Western Pacific Mutual Insurance Company.
            </p>
          </div>
        </div>
      </section>

      {/* Two-Tier Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#262626] mb-4">
              Comprehensive Two-Tier Coverage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your peace of mind starts from day one and continues for a full decade
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Year 1 Coverage */}
            <Card className="border-2 border-[#DAA520] hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#DAA520] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  1
                </div>
                <h3 className="text-3xl font-bold text-[#262626] mb-4">Year 1 Coverage</h3>
                <p className="text-gray-600 mb-6">
                  Comprehensive coverage for workmanship, materials, and mechanical systems
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#262626] mb-3">Structural Elements:</h4>
                    <ul className="space-y-2">
                      {['Basements/Crawl Spaces/Foundation Slabs', 'Interior & Exterior Framing', 'Walls & Wall Covering'].map((item, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <CheckCircle className="text-[#DAA520] flex-shrink-0 mt-1" size={18} />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-[#262626] mb-3">Exterior Elements:</h4>
                    <ul className="space-y-2">
                      {['Doors', 'Roofing', 'Sitework', 'Windows'].map((item, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <CheckCircle className="text-[#DAA520] flex-shrink-0 mt-1" size={18} />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-[#262626] mb-3">Interior Elements:</h4>
                    <ul className="space-y-2">
                      {['Interior Finishes & Trim', 'Floor Covering', 'Cabinets & Countertops'].map((item, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <CheckCircle className="text-[#DAA520] flex-shrink-0 mt-1" size={18} />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-[#262626] mb-3">Systems:</h4>
                    <ul className="space-y-2">
                      {['Chimney & Fireplace', 'Electrical System & Wiring', 'HVAC System & Ductwork', 'Plumbing & Pipe Leaks', 'Water Supply', 'Sewer Lines'].map((item, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <CheckCircle className="text-[#DAA520] flex-shrink-0 mt-1" size={18} />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm font-semibold text-[#DAA520]">Responsibility: The Builder</p>
                </div>
              </CardContent>
            </Card>

            {/* 10 Year Coverage */}
            <Card className="border-2 border-[#DAA520] hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-[#DAA520]/5 to-transparent">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#DAA520] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  10
                </div>
                <h3 className="text-3xl font-bold text-[#262626] mb-4">10-Year Coverage</h3>
                <p className="text-gray-600 mb-6">
                  Long-term protection for designated load-bearing structural elements
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#262626] mb-3">Designated Structural Elements:</h4>
                    <ul className="space-y-2">
                      {[
                        'Concrete Elements',
                        'Masonry Elements', 
                        'Columns',
                        'Wood Foundations',
                        'Wood/Steel Framing'
                      ].map((item, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <CheckCircle className="text-[#DAA520] flex-shrink-0 mt-1" size={18} />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-[#DAA520]/30 mt-6">
                    <h4 className="font-semibold text-[#262626] mb-2">What's a Structural Defect?</h4>
                    <p className="text-sm text-gray-600">
                      Coverage protects against major structural defects in load-bearing portions of the home. These defects often result from soil movement beneath the foundation and are typically <strong>not covered by homeowner's insurance</strong>.
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm font-semibold text-[#DAA520]">Insurer Backed: Western Pacific Mutual Insurance Company</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#262626] mb-4">
              Why Our Warranty Protects You
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Insurance-Backed Protection',
                description: 'Your warranty is backed by Western Pacific Mutual Insurance Company, ensuring coverage continues even if the builder experiences financial difficulties.'
              },
              {
                icon: CheckCircle,
                title: 'Automatic Transferability',
                description: 'The warranty automatically transfers to new homeowners when you sell—no paperwork, no fees, no phone calls required. Adds value to your property.'
              },
              {
                icon: FileText,
                title: 'Free Mediation Services',
                description: 'RWC offers free, neutral third-party mediation to help resolve any disputes between you and the builder, ensuring fair outcomes.'
              }
            ].map((benefit, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#DAA520]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="text-[#DAA520]" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-[#262626] mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Download Documents */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#262626] mb-4">
              Warranty Documentation
            </h2>
            <p className="text-xl text-gray-600">
              Download detailed warranty information and coverage details
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                title: 'Warranty Overview',
                description: 'Consumer infographic explaining your warranty coverage',
                url: 'https://customer-assets.emergentagent.com/job_luxury-homes-ohio/artifacts/dwuj1udd_Consumer%20Inforgraphic%20Flyer%205191-126.pdf'
              },
              {
                title: 'Coverage Details',
                description: 'Complete breakdown of what\'s covered in your warranty',
                url: 'https://customer-assets.emergentagent.com/job_luxury-homes-ohio/artifacts/em0qnqr7_Flyer%20-%20What%27s%20Covered%20-%20CSW%20OH%20-%20593%20CSW%20OH-424.pdf'
              },
              {
                title: 'Full Warranty Book',
                description: 'Complete warranty terms, conditions, and exclusions',
                url: 'https://customer-assets.emergentagent.com/job_luxury-homes-ohio/artifacts/9scjijbb_Warranty%20Book%20-%20CSW%20-%20Ohio%20-%20Sample%20-%203170-725.pdf'
              }
            ].map((doc, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300">
                <CardContent className="p-6">
                  <FileText className="text-[#DAA520] mb-4" size={40} />
                  <h3 className="text-lg font-bold text-[#262626] mb-2">{doc.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{doc.description}</p>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-[#262626] hover:bg-[#DAA520] text-white">
                      <Download size={16} className="mr-2" />
                      Download PDF
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-[#DAA520]">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-[#262626] mb-4">Important Warranty Information</h3>
              <ul className="space-y-3">
                {[
                  'Your warranty becomes effective on the date of closing',
                  'Routine maintenance is required to maintain coverage',
                  'The builder is responsible for Year 1 corrections',
                  'The insurer handles Years 1-10 for designated structural elements',
                  'Warranty automatically transfers with home sale',
                  'Complete warranty book provided at closing with all terms, conditions, and exclusions'
                ].map((note, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#DAA520] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{note}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DAA520]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            Build With Confidence
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Every EC Home Build comes with industry-leading warranty protection. Let's discuss your custom home project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#262626] hover:bg-black text-white px-8 py-6 text-lg">
                Schedule Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/floor-plans">
              <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white text-white hover:bg-white hover:text-[#262626] px-8 py-6 text-lg">
                Browse Floor Plans
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Warranty;
